import { defineI18n } from "fumadocs-core/i18n";
import { defineI18nUI } from "fumadocs-ui/i18n";
import { fumadocsJa } from "@velpha/ui/lib/fumadocs";
import type { BaseLayoutProps } from "fumadocs-ui/layouts/shared";
import Image from "next/image";
import { createElement } from "react";

export const i18n = defineI18n({
  languages: ["ja", "en"],
  defaultLanguage: "ja",
  hideLocale: "never",
  parser: "dir",
});

export type Locale = (typeof i18n.languages)[number];

export function isSupportedLocale(locale: string): locale is Locale {
  return i18n.languages.includes(locale as Locale);
}

export const locales = [
  { code: "ja", label: "日本語" },
  { code: "en", label: "English" },
] as const;

const translations = {
  ja: {
    brand: "Velpha",
    description: "資産運用を、商品名ではなく考える順番から学べる Velpha のドキュメントです。",
    search: "ドキュメントを検索",
    chooseLanguage: "言語を切り替える",
    chooseTheme: "テーマを切り替える",
    light: "ライト",
    dark: "ダーク",
    system: "システム",
    summaryTitle: "このページでわかること",
    nav: {
      docs: "資産運用を学ぶ",
      releaseNotes: "リリースノート",
    },
  },
  en: {
    brand: "Velpha",
    description:
      "Beginner-first documentation for understanding Velpha's stock analysis, portfolio analysis, and optimization workflow.",
    search: "Search docs",
    chooseLanguage: "Choose language",
    chooseTheme: "Choose theme",
    light: "Light",
    dark: "Dark",
    system: "System",
    summaryTitle: "What you'll learn",
    nav: {
      docs: "Analysis Docs",
      releaseNotes: "Release Notes",
    },
  },
} as const satisfies Record<Locale, Record<string, unknown>>;

export function getTranslations(locale: string) {
  return isSupportedLocale(locale) ? translations[locale] : translations.ja;
}

export const i18nUI = defineI18nUI(i18n, {
  ja: fumadocsJa,
  en: { displayName: "English" },
});

const navTitle = createElement(
  "span",
  { className: "inline-flex items-center gap-1.5" },
  createElement(Image, {
    src: "/logo.png",
    alt: "",
    "aria-hidden": true,
    width: 16,
    height: 16,
    className: "size-4",
  }),
  createElement("span", null, "Velpha"),
  createElement(
    "span",
    {
      className: "rounded-md border border-border px-1.5 text-[10px] font-medium leading-5 text-muted-foreground",
    },
    "Docs"
  )
);

export function baseOptions(locale: string): BaseLayoutProps {
  return {
    nav: {
      title: navTitle,
      url: `/${locale}/docs`,
    },
    themeSwitch: { enabled: true },
    i18n: true,
  };
}
